import { Injectable, computed, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { App } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { firstValueFrom } from 'rxjs';
import { PlatformService } from '../platform/platform.service';
import { NotificationApiService } from './notification-api.service';
import { NotificationFilter, UserNotificationItem } from './notification.models';

interface NotificationPageState {
  count: number;
  next: string | null;
  previous: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationStore {
  readonly items = signal<UserNotificationItem[]>([]);
  readonly filter = signal<NotificationFilter>('all');
  readonly isLoadingList = signal(false);
  readonly listError = signal<string | null>(null);

  readonly selectedNotification = signal<UserNotificationItem | null>(null);
  readonly isLoadingDetail = signal(false);
  readonly detailError = signal<string | null>(null);

  readonly unreadCount = signal(0);
  readonly isLoadingUnreadCount = signal(false);
  readonly unreadCountError = signal<string | null>(null);

  readonly page = signal(1);
  readonly pageSize = signal(20);
  readonly pagination = signal<NotificationPageState>({
    count: 0,
    next: null,
    previous: null
  });
  readonly isMarkingAllRead = signal(false);
  readonly markAllReadError = signal<string | null>(null);

  readonly filteredItems = computed(() => {
    return this.items();
  });

  readonly maxPages = computed(() => {
    const total = this.pagination().count;
    const perPage = this.pageSize();
    return total === 0 ? 1 : Math.ceil(total / perPage);
  });

  private unreadCountLoaded = false;
  private unreadCountInvalidated = true;
  private appStateChangeListener: PluginListenerHandle | null = null;

  private readonly handleWindowFocus = (): void => {
    this.refreshUnreadCount(true).catch(() => undefined);
  };

  constructor(
    private readonly apiService: NotificationApiService,
    private readonly platformService: PlatformService
  ) {
    if (this.platformService.isBrowser()) {
      this.platformService.addWindowEventListener('focus', this.handleWindowFocus);
    }

    if (this.platformService.isBrowser() && this.platformService.getRuntimePlatform() === 'capacitor') {
      App.addListener('appStateChange', ({ isActive }) => {
        if (!isActive) {
          return;
        }
        this.refreshUnreadCount(true).catch(() => undefined);
      }).then((listener) => {
        this.appStateChangeListener = listener;
      }).catch(() => undefined);
    }
  }

  async loadList(page = this.page()): Promise<void> {
    this.isLoadingList.set(true);
    this.listError.set(null);

    try {
      const response = await firstValueFrom(this.apiService.listNotifications({
        page,
        page_size: this.pageSize(),
        unread: this.filter() === 'unread'
      }));

      this.page.set(page);
      this.items.set(response.results ?? []);
      this.pagination.set({
        count: response.count,
        next: response.next,
        previous: response.previous
      });
      this.selectedNotification.set(null);
    } catch {
      this.listError.set('common.errors.failed_load');
    } finally {
      this.isLoadingList.set(false);
    }
  }

  async loadDetail(notificationId: number): Promise<void> {
    this.isLoadingDetail.set(true);
    this.detailError.set(null);

    const cached = this.items().find((item) => item.id === notificationId);
    if (cached) {
      this.selectedNotification.set(cached);
    }

    try {
      const notification = await firstValueFrom(this.apiService.getNotification(notificationId));
      this.selectedNotification.set(notification);

      this.items.update((currentItems) =>
        currentItems.map((item) =>
          item.id === notificationId
            ? notification
            : item
        )
      );
    } catch (error: unknown) {
      if (error instanceof HttpErrorResponse && error.status === 404) {
        this.detailError.set('pages.notifications.detail.not_found');
      } else {
        this.detailError.set('common.errors.failed_load');
      }
      this.selectedNotification.set(null);
    } finally {
      this.isLoadingDetail.set(false);
    }
  }

  async refreshUnreadCount(force = false): Promise<void> {
    if (!force && this.unreadCountLoaded && !this.unreadCountInvalidated) {
      return;
    }

    this.isLoadingUnreadCount.set(true);
    this.unreadCountError.set(null);

    try {
      const response = await firstValueFrom(this.apiService.getUnreadCount());
      this.unreadCount.set(Math.max(0, response.count));
      this.unreadCountLoaded = true;
      this.unreadCountInvalidated = false;
    } catch {
      this.unreadCountError.set('common.errors.failed_load');
    } finally {
      this.isLoadingUnreadCount.set(false);
    }
  }

  async markAsRead(notificationId: number): Promise<void> {
    const target = this.items().find((item) => item.id === notificationId) ?? this.selectedNotification();
    if (!target || target.read_at) {
      return;
    }

    try {
      await firstValueFrom(this.apiService.markAsRead(notificationId));

      const nowIso = new Date().toISOString();
      this.items.update((currentItems) =>
        currentItems.map((item) =>
          item.id === notificationId
            ? { ...item, read_at: nowIso, status: 'READ' }
            : item
        )
      );

      if (this.selectedNotification()?.id === notificationId) {
        this.selectedNotification.update((item) =>
          item ? { ...item, read_at: nowIso, status: 'READ' } : item
        );
      }

      this.unreadCount.update((value) => Math.max(0, value - 1));
      this.unreadCountLoaded = true;
      this.unreadCountInvalidated = false;

      if (this.filter() === 'unread') {
        await this.loadList(this.page());
      }
    } catch {
      this.unreadCountInvalidated = true;
      throw new Error('mark-as-read-failed');
    }
  }

  async markAllAsRead(): Promise<number> {
    this.isMarkingAllRead.set(true);
    this.markAllReadError.set(null);

    try {
      const response = await firstValueFrom(this.apiService.markAllAsRead());
      await this.loadList(this.page());
      await this.refreshUnreadCount(true);
      return response.updated;
    } catch {
      this.markAllReadError.set('common.errors.failed_save');
      throw new Error('mark-all-read-failed');
    } finally {
      this.isMarkingAllRead.set(false);
    }
  }

  async setFilter(filter: NotificationFilter): Promise<void> {
    if (this.filter() === filter) {
      return;
    }

    this.filter.set(filter);
    await this.loadList(1);
  }

  async onForegroundPushReceived(): Promise<void> {
    if (this.unreadCountLoaded) {
      this.unreadCount.update((value) => value + 1);
    }
    this.unreadCountInvalidated = true;
    await this.refreshUnreadCount(true);
  }

  invalidateUnreadCount(): void {
    this.unreadCountInvalidated = true;
  }

  async openNextPage(): Promise<void> {
    if (this.page() >= this.maxPages()) {
      return;
    }
    await this.loadList(this.page() + 1);
  }

  async openPreviousPage(): Promise<void> {
    if (this.page() <= 1) {
      return;
    }
    await this.loadList(this.page() - 1);
  }
}
